import React from 'react';
import { Star, X, ChevronRight, MapPin, Zap, Flame, Clock, Ban } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';

const DecisionCard = ({ 
  candidate, 
  benchmark,
  onKeep, 
  onPass, 
  onView,
  onRestore,
  isAnimatingOut,
  animationType,
  categories = [],
  isRejected = false
}) => {
  
  const getVerdict = () => {
    const score = candidate.qualification?.score_total || candidate.ai_analysis?.candidate_score || 0;
    const status = candidate.status;

    // Statuts avancés : le candidat a déjà franchi l'étape d'analyse, on affiche son stade réel.
    const ADVANCED_LABELS = {
      talent_pool: { label: 'EN POOL', color: 'bg-[#4C8F6E]', textColor: 'text-white' },
      assigned: { label: 'ASSIGNÉ', color: 'bg-[#4C8F6E]', textColor: 'text-white' },
      interviewed: { label: 'ENTRETIEN FAIT', color: 'bg-[#4C8F6E]/80', textColor: 'text-white' },
      to_call: { label: 'ENTRETIEN PLANIF.', color: 'bg-[#0F2744]/70', textColor: 'text-white' },
      validated: { label: 'SÉLECTIONNÉ', color: 'bg-[#0F2744]/70', textColor: 'text-white' },
      prequalified: { label: 'SÉLECTIONNÉ', color: 'bg-[#0F2744]/70', textColor: 'text-white' },
      rejected: { label: 'REJETÉ', color: 'bg-red-500/80', textColor: 'text-white' },
      rejected_auto: { label: 'REJETÉ', color: 'bg-red-500/80', textColor: 'text-white' },
      archived: { label: 'ARCHIVÉ', color: 'bg-gray-400', textColor: 'text-white' },
    };
    if (ADVANCED_LABELS[status]) {
      return ADVANCED_LABELS[status];
    }

    // Statuts initiaux : verdict basé sur le score (comportement historique)
    const isQualified = candidate.qualification?.decision === 'A_RECRUTER';

    if (score >= 75 && isQualified) {
      return { label: 'RECOMMANDÉ', color: 'bg-[#4C8F6E]', textColor: 'text-white' };
    } else if (score >= 55 || !isQualified) {
      return { label: 'À ANALYSER', color: 'bg-[#0F2744]', textColor: 'text-white' };
    } else {
      return { label: 'FAIBLE MATCH', color: 'bg-[#0F2744]/20', textColor: 'text-[#0F2744]' };
    }
  };

  const getPrioritySignal = () => {
    if (candidate.availability === 'immediate' || candidate.availability === 'immediat') {
      return { icon: Zap, label: 'Dispo. immédiate', color: 'text-[#4C8F6E]' };
    }
    if (candidate.shortlist_count && candidate.shortlist_count >= 3) {
      return { icon: Flame, label: 'Très demandé', color: 'text-[#0F2744]' };
    }
    const daysSinceCreation = Math.floor(
      (new Date() - new Date(candidate.created_at)) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceCreation > 5) {
      return { icon: Clock, label: `En attente ${daysSinceCreation}j`, color: 'text-gray-400' };
    }
    return null;
  };

  const getTimeSignal = () => {
    const created = new Date(candidate.created_at);
    const now = new Date();
    const diffMs = now - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHours < 24) {
      return { isNew: true, label: `${diffHours}h` };
    } else if (diffDays < 7) {
      return { isNew: false, label: `${diffDays}j` };
    } else {
      const weeks = Math.floor(diffDays / 7);
      return { isNew: false, label: `${weeks} sem.` };
    }
  };

  const getPricingSignal = () => {
    const salary = candidate.expected_salary || candidate.pricing?.recommended_price_monthly || 0;
    if (!salary || !benchmark?.salary_eur_median) {
      return { label: `${salary}€`, signal: null, color: 'text-gray-600' };
    }
    const median = benchmark.salary_eur_median;
    const diff = Math.round(((salary - median) / median) * 100);
    if (diff <= -15) {
      return { label: `${salary}€`, signal: `-${Math.abs(diff)}%`, color: 'text-[#4C8F6E]' };
    } else if (diff >= 15) {
      return { label: `${salary}€`, signal: `+${diff}%`, color: 'text-[#0F2744]' };
    } else {
      return { label: `${salary}€`, signal: '≈ marché', color: 'text-gray-500' };
    }
  };

  const getCategoryBadge = () => {
    // Prefer auto-detected category enriched by backend (id + label_fr + color)
    if (candidate.detected_category?.label_fr) {
      return {
        label: candidate.detected_category.label_fr,
        color: candidate.detected_category.color || '#0F2744',
      };
    }
    // Fallback: legacy mapping
    const catId = candidate.primary_category || candidate.job_category;
    const LABELS = {
      'assistant_virtuel': 'Assistant Virtuel',
      'community_manager': 'Community Manager',
      'gestion_ads': 'Gestion Ads / Traffic Manager',
      'seo': 'SEO / Référencement',
      'developpeur': 'Développeur / Tech',
      'graphiste': 'Graphiste / Designer',
      'redacteur': 'Rédacteur / Copywriter',
      'comptabilite': 'Comptabilité / Finance',
      'support_client': 'Support Client',
      'commercial': 'Commercial / Vente',
      'ai_automation': 'AI Automation Specialist',
      'funnel_builder': 'Funnel Builder',
      'video_editor': 'Monteur Vidéo / Video Editor',
      'autre': 'Autre',
    };
    const label = (catId && LABELS[catId])
      || categories.find(c => c._id === catId || c.id === catId)?.name
      || candidate.main_profession
      || 'Non défini';
    return { label, color: '#0F2744' };
  };

  const getLevelLabel = () => {
    const years = candidate.years_of_experience || 0;
    if (years <= 2) return 'Junior';
    if (years <= 5) return 'Confirmé';
    return 'Senior';
  };

  const COUNTRIES = {
    'Madagascar': 'Madagascar', 'Senegal': 'Sénégal', 'Benin': 'Bénin',
    'Morocco': 'Maroc', 'Tunisia': 'Tunisie', 'Cameroon': 'Cameroun',
    'benin': 'Bénin', 'Côte d\'Ivoire': 'Côte d\'Ivoire', 'France': 'France',
    'Maroc': 'Maroc', 'Tunisie': 'Tunisie', 'Cameroun': 'Cameroun',
    'Maurice': 'Maurice'
  };
  const countryName = COUNTRIES[candidate.country] || candidate.country_name || candidate.country;

  const verdict = getVerdict();
  const prioritySignal = getPrioritySignal();
  const timeSignal = getTimeSignal();

  return (
    <div 
      className={cn(
        "bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300",
        isAnimatingOut && animationType === 'keep' && "border-[#4C8F6E] bg-[#4C8F6E]/5 scale-95 opacity-0",
        isAnimatingOut && animationType === 'pass' && "translate-x-[-100%] opacity-0"
      )}
      data-testid={`decision-card-${candidate.id}`}
    >
      {/* VERDICT */}
      <div className="px-4 pt-4 pb-2">
        <span className={cn(
          "inline-block px-3 py-1 rounded-md text-xs font-bold tracking-wide",
          verdict.color, verdict.textColor
        )}>
          {verdict.label}
        </span>
      </div>

      {/* PRIORITÉ + TEMPS */}
      <div className="px-4 pb-3 flex items-center gap-3 text-xs">
        {prioritySignal && (
          <span className={cn("flex items-center gap-1 font-medium", prioritySignal.color)}>
            <prioritySignal.icon className="h-3.5 w-3.5" />
            {prioritySignal.label}
          </span>
        )}
        {prioritySignal && timeSignal && <span className="text-gray-300">|</span>}
        <span className={cn(
          "flex items-center gap-1",
          timeSignal.isNew ? "text-[#4C8F6E] font-medium" : "text-gray-400"
        )}>
          {timeSignal.isNew && <span className="bg-[#4C8F6E]/10 text-[#4C8F6E] px-1.5 py-0.5 rounded text-[10px] font-bold">NOUVEAU</span>}
          {timeSignal.label}
        </span>
      </div>

      <div className="border-t border-gray-100 mx-4" />

      {/* IDENTITÉ */}
      <div className="px-4 py-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {candidate.first_name} {candidate.last_name}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {(() => {
            // Prefer the full tag list when available (multi-category)
            const tags = candidate.category_tags || [];
            if (tags.length > 0) {
              return tags.slice(0, 3).map((t, idx) => (
                <span
                  key={`tag-${t.id || idx}`}
                  className="inline-block px-2 py-0.5 text-xs font-medium rounded-md"
                  style={{ backgroundColor: `${t.color || '#0F2744'}1A`, color: t.color || '#0F2744' }}
                  data-testid={`tag-${candidate.id}-${t.id || idx}`}
                >
                  {t.label_fr}
                </span>
              ));
            }
            const cb = getCategoryBadge();
            return (
              <span
                className="inline-block px-2 py-0.5 text-xs font-medium rounded-md"
                style={{ backgroundColor: `${cb.color}1A`, color: cb.color }}
                data-testid={`category-label-${candidate.id}`}
              >
                {cb.label}
              </span>
            );
          })()}
          <span className="text-xs text-gray-400">|</span>
          <span className="text-xs text-gray-500">{getLevelLabel()}</span>
        </div>
      </div>

      {/* CONTEXTE */}
      <div className="px-4 pb-2 space-y-1.5">
        <div className="flex items-center gap-3 text-sm flex-wrap">
          <span className="flex items-center gap-1.5 text-gray-600">
            <MapPin className="h-4 w-4 text-gray-400" />
            {countryName}
          </span>
          {candidate.expected_salary_local ? (
            <span className="flex items-center gap-1 text-[#0F2744] font-medium text-xs sm:text-sm truncate" data-testid={`salary-${candidate.id}`}>
              {candidate.contract_type === 'partial' && candidate.partial_percentage
                ? `${candidate.partial_percentage}%`
                : '100%'
              } — {Number(candidate.expected_salary_local).toLocaleString('fr-FR')} {candidate.currency || ''}/mois
            </span>
          ) : (
            <span className="text-xs text-gray-400 italic">Salaire non renseigné</span>
          )}
        </div>
        {/* Evaluation status */}
        <div className="flex items-center gap-2 flex-wrap">
          {!candidate.evaluation_sent && !candidate.qualification_score && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full border border-gray-200" data-testid={`eval-none-${candidate.id}`}>
              Évaluation non envoyée
            </span>
          )}
          {candidate.evaluation_sent && !candidate.qualification_score && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#0F2744]/10 text-[#0F2744] rounded-full border border-[#0F2744]/20" data-testid={`eval-sent-${candidate.id}`}>
              <Clock className="h-3 w-3" />
              En attente de réponse
            </span>
          )}
          {candidate.qualification_score > 0 && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium bg-[#4C8F6E]/10 text-[#4C8F6E] border-[#4C8F6E]/20" data-testid={`eval-score-${candidate.id}`}>
              Formulaire rempli — {candidate.qualification_score}/100
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100 mx-4" />

      {/* ACTIONS */}
      <div className="p-3 sm:p-4 flex gap-2 sm:gap-3">
        {isRejected ? (
          <>
            <button
              onClick={onRestore}
              className="flex-1 flex items-center justify-center gap-1.5 bg-[#4C8F6E] hover:bg-[#3d7a5c] text-white font-medium py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors text-sm"
              data-testid={`restore-candidate-${candidate.id}`}
            >
              <Star className="h-4 w-4 flex-shrink-0" />
              <span>Restaurer</span>
            </button>
            <button
              onClick={onView}
              className="flex-1 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors text-sm"
              data-testid={`view-candidate-${candidate.id}`}
            >
              <span>Profil</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onKeep}
              className="flex-1 flex items-center justify-center gap-1.5 bg-[#4C8F6E] hover:bg-[#3d7a5c] text-white font-medium py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors text-sm"
              data-testid={`keep-candidate-${candidate.id}`}
            >
              <Star className="h-4 w-4 flex-shrink-0" />
              <span>Garder</span>
            </button>
            <button
              onClick={onPass}
              className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 font-medium py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors border border-red-200 text-sm"
              data-testid={`reject-candidate-${candidate.id}`}
            >
              <Ban className="h-4 w-4 flex-shrink-0" />
              <span>Rejeter</span>
            </button>
            <button
              onClick={onView}
              className="flex-1 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors text-sm"
              data-testid={`view-candidate-${candidate.id}`}
            >
              <span>Profil</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DecisionCard;
